import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SessionAutosaveController,
  type SessionAutosaveStatus,
} from '../lib/sessionAutosaveController';

export type { SessionAutosaveStatus };

export interface UseSessionAutosaveOptions<T> {
  /** Current local value. */
  value: T;
  /** Persist the latest value. Must update the canonical record (not create duplicates). */
  persist: (value: T) => Promise<void>;
  /** Debounce before automatic persist. */
  debounceMs: number;
  /** When false, never schedules or flushes. */
  enabled?: boolean;
  /**
   * Identity for the save context (e.g. appointment id + load epoch).
   * Changing this adopts the current value as the clean baseline and ignores in-flight responses.
   */
  contextKey?: string;
  /** Skip persist when equal to last successfully saved value. */
  isEqual?: (a: T, b: T) => boolean;
}

export interface UseSessionAutosaveResult {
  status: SessionAutosaveStatus;
  error: string | null;
  /** Flush pending debounce and persist if dirty. */
  flush: () => Promise<void>;
  /** Re-attempt persist after an error. */
  retry: () => Promise<void>;
}

/**
 * Lightweight autosave for Zoom sidebar fields (scratch notes, attendance selects).
 * Not a replacement for progress-note QR autosave (`useProviderNoteAutosave`).
 */
export function useSessionAutosave<T>(options: UseSessionAutosaveOptions<T>): UseSessionAutosaveResult {
  const {
    value,
    persist,
    debounceMs,
    enabled = true,
    contextKey = '',
    isEqual,
  } = options;

  const [status, setStatus] = useState<SessionAutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const controllerRef = useRef<SessionAutosaveController<T> | null>(null);
  if (controllerRef.current == null) {
    controllerRef.current = new SessionAutosaveController(value, {
      persist,
      isEqual,
      onStatus: (nextStatus, nextError) => {
        setStatus(nextStatus);
        setError(nextError);
      },
    });
  }

  const controller = controllerRef.current;
  controller.updatePersist(persist);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextScheduleRef = useRef(true);
  const valueRef = useRef(value);
  valueRef.current = value;

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    if (!enabled) return;
    clearDebounce();
    setStatus((prev) => (prev === 'saving' || prev === 'retrying' ? prev : 'dirty'));
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void controller.run('autosave');
    }, debounceMs);
  }, [clearDebounce, debounceMs, enabled, controller]);

  const flush = useCallback(async () => {
    if (!enabled) return;
    clearDebounce();
    await controller.run('flush');
  }, [clearDebounce, controller, enabled]);

  const retry = useCallback(async () => {
    if (!enabled) return;
    clearDebounce();
    await controller.run('retry');
  }, [clearDebounce, controller, enabled]);

  useEffect(() => {
    clearDebounce();
    controller.setContextKey(contextKey, valueRef.current);
    skipNextScheduleRef.current = true;
    setStatus('idle');
    setError(null);
  }, [contextKey, clearDebounce, controller]);

  useEffect(() => {
    controller.setValue(value);
    if (!enabled) return;
    if (skipNextScheduleRef.current) {
      skipNextScheduleRef.current = false;
      return;
    }
    schedule();
    return clearDebounce;
  }, [value, enabled, schedule, clearDebounce, controller]);

  useEffect(() => () => {
    clearDebounce();
    if (!enabled) return;
    void controller.run('flush').catch(() => undefined);
  }, [clearDebounce, enabled, controller]);

  return { status, error, flush, retry };
}
